import { createFileRoute } from "@tanstack/react-router";
import { Component, useEffect, useMemo, useState, type ReactNode } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, AlertCircle } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { SortableImageGallery } from "@/components/admin/SortableImageGallery";
import { SpecCombobox } from "@/components/admin/SpecCombobox";
import { getAdminToken } from "@/lib/admin/session";
import {
  listProducts,
  upsertProduct,
  deleteProduct,
  toggleProductFlag,
  listSpecPresets,
  createSpecLabel,
  createSpecValue,
} from "@/lib/admin/cms.functions";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/admin/prodotti/")({
  head: () => ({ meta: [{ title: "Prodotti · Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminProducts,
});

type Product = {
  id: string;
  slug: string;
  title: string;
  short_description: string;
  long_description: string;
  category: string;
  animal: string;
  price: number | "";
  price_unit: "kg" | "pezzo";
  characteristics: { label: string; value: string }[];
  images: string[];
  featured: boolean;
  bookable: boolean;
  out_of_stock: boolean;
  status: "published" | "draft";
  sort_order: number;
  on_offer: boolean;
  offer_price: number | null;
};

type SpecLabel = { id: string; label: string; unit: string | null };
type SpecValue = { id: string; label_id: string; value: string };

const CATEGORIES = [
  "selezione-macellaio",
  "piatti-pronti",
  "preparati",
  "insaccati",
  "abatina",
  "preconfezionati",
];
const ANIMALS = ["manzo", "vitello", "maiale", "pollo", "tacchino", "agnello", "selvaggina", "vari"];

function emptyProduct(): Product {
  return {
    id: "",
    slug: "",
    title: "",
    short_description: "",
    long_description: "",
    category: "selezione-macellaio",
    animal: "manzo",
    price: "",
    price_unit: "kg",
    characteristics: [],
    images: [],
    featured: false,
    bookable: true,
    out_of_stock: false,
    status: "published",
    sort_order: 0,
    on_offer: false,
    offer_price: null,
  };
}

/** Normalizza un prodotto proveniente dal backend in una forma sicura per il form. */
function normalizeProduct(raw: unknown): Product {
  const p = (raw ?? {}) as Record<string, unknown>;
  const chRaw = Array.isArray(p.characteristics) ? p.characteristics : [];
  const characteristics = chRaw.map((c) => {
    if (typeof c === "string") return { label: "Nota", value: c };
    if (c && typeof c === "object") {
      const o = c as Record<string, unknown>;
      const label = typeof o.label === "string" ? o.label : "";
      const value =
        typeof o.value === "string"
          ? o.value
          : o.value == null
            ? ""
            : String(o.value);
      return { label, value };
    }
    return { label: "", value: c == null ? "" : String(c) };
  });
  const images = Array.isArray(p.images)
    ? p.images.filter((x): x is string => typeof x === "string" && x.length > 0)
    : [];
  const priceNum = typeof p.price === "number" ? p.price : Number(p.price);
  return {
    id: typeof p.id === "string" ? p.id : "",
    slug: typeof p.slug === "string" ? p.slug : "",
    title: typeof p.title === "string" ? p.title : "",
    short_description: typeof p.short_description === "string" ? p.short_description : "",
    long_description: typeof p.long_description === "string" ? p.long_description : "",
    category: typeof p.category === "string" ? p.category : "selezione-macellaio",
    animal: typeof p.animal === "string" ? p.animal : "manzo",
    price: Number.isFinite(priceNum) ? priceNum : "",
    price_unit: p.price_unit === "pezzo" ? "pezzo" : "kg",
    characteristics,
    images,
    featured: Boolean(p.featured),
    bookable: p.bookable !== false,
    out_of_stock: Boolean(p.out_of_stock),
    status: p.status === "draft" ? "draft" : "published",
    sort_order: typeof p.sort_order === "number" ? p.sort_order : Number(p.sort_order) || 0,
    on_offer: Boolean(p.on_offer),
    offer_price:
      p.offer_price == null || p.offer_price === ""
        ? null
        : Number(p.offer_price),
  };
}

class FormErrorBoundary extends Component<
  { onClose: () => void; children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    console.error("[admin/prodotti] edit form crash:", error);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="mt-6 space-y-3 border border-red-200 bg-red-50 rounded-md p-4">
          <div className="font-medium text-red-800">Errore nel form di modifica</div>
          <div className="text-sm text-red-700 whitespace-pre-wrap break-words">
            {this.state.error.message || String(this.state.error)}
          </div>
          <button
            type="button"
            className="text-sm underline text-red-800"
            onClick={this.props.onClose}
          >
            Chiudi
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AdminProducts() {
  const fetchList = useServerFn(listProducts);
  const save = useServerFn(upsertProduct);
  const remove = useServerFn(deleteProduct);
  const toggle = useServerFn(toggleProductFlag);
  const fetchPresets = useServerFn(listSpecPresets);
  const createLabel = useServerFn(createSpecLabel);
  const createValue = useServerFn(createSpecValue);
  const [items, setItems] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Product | null>(null);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState<string>("all");
  const [labels, setLabels] = useState<SpecLabel[]>([]);
  const [values, setValues] = useState<SpecValue[]>([]);

  const featuredCount = items.filter((p) => p.featured).length;

  async function refresh() {
    const token = getAdminToken();
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetchList({ data: { token } });
      const rows = Array.isArray(res.products) ? res.products : [];
      setItems(rows.map((r) => normalizeProduct(r)));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }

  async function refreshPresets() {
    const token = getAdminToken();
    if (!token) return;
    try {
      const res = await fetchPresets({ data: { token } });
      setLabels(res.labels as SpecLabel[]);
      setValues(res.values as SpecValue[]);
      if (res.error) toast.warning(res.error);
    } catch {
      // silent
    }
  }

  useEffect(() => {
    refresh();
    refreshPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSave() {
    if (!editing) return;
    const token = getAdminToken();
    if (!token) return;
    if (!editing.title.trim()) {
      toast.error("Inserisci un titolo");
      return;
    }
    if (editing.price === "") {
      toast.error("Inserisci il prezzo");
      return;
    }
    try {
      await save({
        data: {
          token,
          id: editing.id || null,
          product: {
            slug: editing.slug || null,
            title: editing.title,
            short_description: editing.short_description,
            long_description: editing.long_description,
            category: editing.category,
            animal: editing.animal,
            price: Number(editing.price),
            price_unit: editing.price_unit,
            characteristics: editing.characteristics.filter((c) => c.label && c.value),
            images: editing.images,
            featured: editing.featured,
            bookable: editing.bookable,
            out_of_stock: editing.out_of_stock,
            status: editing.status,
            sort_order: Number(editing.sort_order) || 0,
            on_offer: editing.on_offer,
            offer_price: editing.on_offer && editing.offer_price != null ? Number(editing.offer_price) : null,
          },
        },
      });
      toast.success("Prodotto salvato");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Eliminare questo prodotto?")) return;
    const token = getAdminToken();
    if (!token) return;
    try {
      await remove({ data: { token, id } });
      toast.success("Eliminato");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }

  async function handleToggle(id: string, field: "featured" | "out_of_stock", value: boolean) {
    const token = getAdminToken();
    if (!token) return;
    try {
      await toggle({ data: { token, id, field, value } });
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }

  const filtered = items.filter((p) => {
    if (filterCat !== "all" && p.category !== filterCat) return false;
    if (search && !p.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const labelOptions = useMemo(() => labels.map((l) => l.label), [labels]);
  const valuesByLabel = useMemo(() => {
    const map = new Map<string, string[]>(); // labelText -> values[]
    labels.forEach((l) => {
      map.set(
        l.label.toLowerCase(),
        values.filter((v) => v.label_id === l.id).map((v) => v.value),
      );
    });
    return map;
  }, [labels, values]);
  const unitByLabel = useMemo(() => {
    const map = new Map<string, string | null>();
    labels.forEach((l) => map.set(l.label.toLowerCase(), l.unit ?? null));
    return map;
  }, [labels]);

  async function handleCreateLabel(label: string) {
    const token = getAdminToken();
    if (!token) return;
    try {
      await createLabel({ data: { token, label } });
      await refreshPresets();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }

  async function handleCreateValue(labelText: string, value: string) {
    const token = getAdminToken();
    if (!token) return;
    const labelRow = labels.find((l) => l.label.toLowerCase() === labelText.toLowerCase());
    if (!labelRow) {
      // Etichetta non ancora salvata come preset: niente da fare per il valore preset
      return;
    }
    try {
      await createValue({ data: { token, label_id: labelRow.id, value } });
      await refreshPresets();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore");
    }
  }

  const featuredDisabled = editing && !editing.featured && featuredCount >= 4;

  return (
    <AdminShell>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl text-slate-900">Prodotti</h1>
            <p className="text-sm text-slate-600">
              {items.length} prodotti totali · <span className="text-amber-600 font-medium">★ {featuredCount}/4 in evidenza</span>
            </p>
          </div>
          <Button onClick={() => setEditing(emptyProduct())} className="bg-brand-red hover:bg-brand-red-hover">
            <Plus className="h-4 w-4 mr-2" /> Nuovo prodotto
          </Button>
        </div>

        <div className="flex flex-wrap gap-3">
          <Input
            placeholder="Cerca per nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Nessun prodotto</div>
          ) : (
            <div className="divide-y divide-slate-200">
              {filtered.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4 hover:bg-slate-50">
                  <div className="w-14 h-14 rounded bg-slate-100 overflow-hidden shrink-0">
                    {p.images[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{p.title}</span>
                      {p.featured && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
                      {p.out_of_stock && (
                        <span className="text-[10px] uppercase px-1.5 py-0.5 bg-red-100 text-red-700 rounded">Esaurito</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{p.category} · {p.animal} · €{Number(p.price).toFixed(2)}/{p.price_unit}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => handleToggle(p.id, "featured", !p.featured)} title="In evidenza">
                      <Star className={`h-4 w-4 ${p.featured ? "fill-amber-500 text-amber-500" : ""}`} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleToggle(p.id, "out_of_stock", !p.out_of_stock)} title="Esaurito">
                      <AlertCircle className={`h-4 w-4 ${p.out_of_stock ? "text-red-600" : ""}`} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(normalizeProduct(p))}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <SheetContent className="sm:max-w-2xl w-full overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{editing?.id ? "Modifica prodotto" : "Nuovo prodotto"}</SheetTitle>
            </SheetHeader>
            {editing && (
              <FormErrorBoundary onClose={() => setEditing(null)}>
              <div className="mt-6 space-y-4 pb-12">
                <div>
                  <Label>Nome *</Label>
                  <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                </div>
                <div>
                  <Label>Slug (lascia vuoto per generarlo)</Label>
                  <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Categoria</Label>
                    <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Animale</Label>
                    <Select value={editing.animal} onValueChange={(v) => setEditing({ ...editing, animal: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{ANIMALS.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Prezzo (€)</Label>
                    <Input type="number" min="0" step="0.01" value={editing.price} placeholder="" onChange={(e) => setEditing({ ...editing, price: e.target.value === "" ? "" : Number(e.target.value) })} />
                  </div>
                  <div>
                    <Label>Unità</Label>
                    <Select value={editing.price_unit} onValueChange={(v) => setEditing({ ...editing, price_unit: v as "kg" | "pezzo" })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">al kg</SelectItem>
                        <SelectItem value="pezzo">al pezzo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Descrizione breve</Label>
                  <Textarea rows={2} value={editing.short_description} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} />
                </div>
                <div>
                  <Label>Descrizione lunga</Label>
                  <Textarea rows={5} value={editing.long_description} onChange={(e) => setEditing({ ...editing, long_description: e.target.value })} />
                </div>
                <SortableImageGallery
                  value={editing.images}
                  onChange={(v) => setEditing({ ...editing, images: v })}
                  bucket="product-images"
                  multiple
                />
                <div>
                  <Label>Caratteristiche</Label>
                  <div className="space-y-2 mt-2">
                    {editing.characteristics.map((c, i) => {
                      const unit = c.label ? unitByLabel.get(c.label.toLowerCase()) ?? null : null;
                      return (
                      <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                        <SpecCombobox
                          value={c.label}
                          onChange={(v) => {
                            const next = [...editing.characteristics];
                            next[i] = { ...next[i], label: v };
                            setEditing({ ...editing, characteristics: next });
                          }}
                          options={labelOptions}
                          onCreate={(v) => handleCreateLabel(v)}
                          placeholder="Etichetta (es. Frollatura)"
                          createLabel="Crea etichetta"
                        />
                        {unit ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="any"
                              value={c.value.replace(new RegExp(`\\s*${unit}$`), "")}
                              onChange={(e) => {
                                const next = [...editing.characteristics];
                                next[i] = { ...next[i], value: e.target.value ? `${e.target.value} ${unit}` : "" };
                                setEditing({ ...editing, characteristics: next });
                              }}
                              placeholder="Valore"
                              className="flex-1"
                            />
                            <span className="text-sm text-slate-500 shrink-0 w-14">{unit}</span>
                          </div>
                        ) : (
                          <SpecCombobox
                            value={c.value}
                            onChange={(v) => {
                              const next = [...editing.characteristics];
                              next[i] = { ...next[i], value: v };
                              setEditing({ ...editing, characteristics: next });
                            }}
                            options={c.label ? (valuesByLabel.get(c.label.toLowerCase()) ?? []) : []}
                            onCreate={(v) => handleCreateValue(c.label, v)}
                            placeholder={c.label ? "Valori (uno o più)" : "Scegli prima un'etichetta"}
                            createLabel="Crea valore"
                            multiple
                          />
                        )}
                        <Button type="button" variant="ghost" size="sm" onClick={() => setEditing({ ...editing, characteristics: editing.characteristics.filter((_, idx) => idx !== i) })}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      );
                    })}
                    <Button type="button" variant="outline" size="sm" onClick={() => setEditing({ ...editing, characteristics: [...editing.characteristics, { label: "", value: "" }] })}>+ aggiungi</Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <label className={`flex items-center justify-between p-3 border rounded-md ${featuredDisabled ? "opacity-50" : ""}`} title={featuredDisabled ? "Massimo 4 prodotti in evidenza" : undefined}>
                    <span className="text-sm">In evidenza {featuredDisabled && <span className="text-xs text-slate-500">(max 4)</span>}</span>
                    <Switch checked={editing.featured} disabled={!!featuredDisabled} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} />
                  </label>
                  <label className="flex items-center justify-between p-3 border rounded-md">
                    <span className="text-sm">Prenotabile</span>
                    <Switch checked={editing.bookable} onCheckedChange={(v) => setEditing({ ...editing, bookable: v })} />
                  </label>
                  <label className="flex items-center justify-between p-3 border rounded-md">
                    <span className="text-sm">Esaurito</span>
                    <Switch checked={editing.out_of_stock} onCheckedChange={(v) => setEditing({ ...editing, out_of_stock: v })} />
                  </label>
                  <label className="flex items-center justify-between p-3 border rounded-md">
                    <span className="text-sm">Pubblicato</span>
                    <Switch checked={editing.status === "published"} onCheckedChange={(v) => setEditing({ ...editing, status: v ? "published" : "draft" })} />
                  </label>
                </div>
                <div className="border border-amber-200 bg-amber-50/40 rounded-md p-3 space-y-3">
                  <label className="flex items-center justify-between">
                    <span className="text-sm font-medium">In offerta</span>
                    <Switch
                      checked={editing.on_offer}
                      onCheckedChange={(v) => setEditing({ ...editing, on_offer: v, offer_price: v ? (editing.offer_price ?? (editing.price === "" ? null : editing.price)) : null })}
                    />
                  </label>
                  {editing.on_offer && (
                    <div>
                      <Label>Prezzo scontato (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editing.offer_price ?? ""}
                        onChange={(e) => setEditing({ ...editing, offer_price: e.target.value === "" ? null : parseFloat(e.target.value) })}
                        placeholder={editing.price === "" ? "Prezzo scontato" : `Inferiore a € ${Number(editing.price).toFixed(2)}`}
                      />
                      {editing.price !== "" && editing.offer_price != null && editing.offer_price >= editing.price && (
                        <p className="text-xs text-amber-700 mt-1">Il prezzo scontato dovrebbe essere inferiore al prezzo normale.</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 pt-4">
                  <Button onClick={handleSave} className="bg-brand-red hover:bg-brand-red-hover">Salva</Button>
                  <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
                </div>
              </div>
              </FormErrorBoundary>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AdminShell>
  );
}